// Keep these limits local to account uploads; other API/auth flows retain their
// existing timeout behavior. Large mobile uploads get two minutes to complete.
export const PROFILE_UPLOAD_TIMEOUT_MS = 120000
export const PROFILE_STATUS_TIMEOUT_MS = 15000

// /user/my omits subscription details supplied by /subscription/status.
// Refresh only status fields, retaining both those details and the local draft.
export const mergeProfileStatus = (account, data) => {
  const fields = [
    'approved', 'awaitingApproval', 'rejectedApproval', 'approval',
    'approvalStanding', 'accountBlocked', 'subscription', 'canUseGatedFeatures',
  ]
  const status = fields.reduce((result, key) => (
    Object.prototype.hasOwnProperty.call(data, key) ? { ...result, [key]: data[key] } : result
  ), {})
  if (status.subscription) {
    status.subscription = { ...account.subscription, ...status.subscription }
  }
  return { ...account, ...status }
}

const selectedFileValid = (file, extensions) => (
  typeof File !== 'undefined'
  && file instanceof File
  && file.size > 0
  && extensions.test(file.name)
)

export const degreeFileIsValid = file => selectedFileValid(file, /\.(jpe?g|png|pdf)$/i)

export const profileValidationError = (account, degreeFile, profileFile) => {
  if (!account) return 'accountProfileSave.unavailable'
  if (account.accountBlocked && !account.approved) return 'accountProfileSave.blocked'
  if (!String(account.name || '').trim() || !String(account.surname || '').trim()) {
    return 'accountProfileSave.nameRequired'
  }
  if (String(account.name).trim().length > 100) return 'accountProfileSave.nameTooLong'
  if (!account.approved && !account.awaitingApproval) {
    if (!/^\d{10,11}$/.test(String(account.identificationNumber || ''))) {
      return 'accountProfileSave.identificationRequired'
    }
    if (!degreeFile) return 'accountProfileSave.degreeRequired'
    if (!degreeFileIsValid(degreeFile)) return 'accountProfileSave.degreeInvalid'
  }
  if (profileFile && !selectedFileValid(profileFile, /\.(jpe?g|png|gif)$/i)) {
    return 'accountProfileSave.avatarInvalid'
  }
  return ''
}

export const profileSaveErrorKey = error => {
  const status = error.response && error.response.status
  const code = error.response && error.response.data && error.response.data.errorCode
  if (Number(code) === 403013) return 'accountProfileSave.blocked'
  if (status === 401 || status === 403) return 'accountProfileSave.sessionExpired'
  if (status === 413) return 'accountProfileSave.uploadTooLarge'
  if (status === 400 || status === 422) return 'accountProfileSave.invalidInformation'
  if (status === 429) return 'accountProfileSave.rateLimited'
  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') return 'accountProfileSave.timeout'
  return 'accountProfileSave.uncertain'
}

export const profileSaveOutcomeUncertain = error => (
  !error.response || error.response.status >= 500 || error.response.status === 408
)

export const logProfileSaveFailure = (stage, error) => {
  // Never log the Axios error/config: it contains FormData, identity fields,
  // document names, and credentials. Only bounded diagnostic metadata belongs here.
  const status = error.response && error.response.status
  const code = error.response && error.response.data && error.response.data.errorCode
  // eslint-disable-next-line no-console
  console.warn('[account-profile-save]', {
    stage,
    status: Number(status) || null,
    errorCode: Number(code) || null,
    timeout: error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT',
  })
}
